import React, { useState, useEffect } from 'react';
import { Calendar, Heart, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { fetchDailyQuotesHistory } from '../services/api';
import { DailyQuoteHistoryItem, DailyQuotesHistoryResponse } from '../types';

interface HistoryViewProps {}

const HistoryView: React.FC<HistoryViewProps> = () => {
  const [history, setHistory] = useState<DailyQuoteHistoryItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response: DailyQuotesHistoryResponse = await fetchDailyQuotesHistory(page, 10);
      setHistory(response.history);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError('Erreur lors du chargement de l\'historique');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadHistory(newPage);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => loadHistory(currentPage)}
            className="mt-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Calendar className="w-6 h-6 mr-2 text-pink-500" />
          Historique des Citations Quotidiennes
        </h2>
        <p className="text-gray-600 mt-2">
          Retrouvez toutes les citations qui ont été diffusées jour après jour
        </p>
      </div>

      {history.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Aucune citation dans l'historique</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {history.map((item) => (
              <div
                key={item.id}
                className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span className="font-medium">{formatDate(item.date)}</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>Créé le {formatDateTime(item.created_at)}</span>
                  </div>
                </div>

                <blockquote className="border-l-4 border-pink-400 pl-4 mb-4">
                  <p className="text-lg text-gray-800 italic leading-relaxed">
                    "{item.message_text}"
                  </p>
                </blockquote>

                <div className="flex items-center justify-between">
                  <div className="flex items-center text-pink-600">
                    <Heart className="w-4 h-4 mr-1 fill-current" />
                    <span className="font-medium">Pour {item.name_text}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    ID: {item.message_id} / {item.name_id}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-8 space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </button>

              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage > totalPages - 3) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-pink-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          )}

          <div className="text-center mt-4 text-sm text-gray-500">
            Page {currentPage} sur {totalPages}
          </div>
        </>
      )}
    </div>
  );
};

export default HistoryView;
